const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "mark",
  "sub",
  "sup",
  "span",
  "div",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "code",
  "pre",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "colgroup",
  "col",
  "iframe",
  "img",
]);

const DROP_CONTENT_TAGS = new Set([
  "script",
  "style",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "select",
  "textarea",
  "meta",
  "link",
]);

const ALLOWED_GLOBAL_ATTRIBUTES = new Set(["class", "style"]);

const ALLOWED_ATTRIBUTES_BY_TAG = {
  a: new Set(["href", "target", "rel", "title"]),
  img: new Set(["src", "alt", "title", "loading", "decoding", "data-size"]),
  iframe: new Set([
    "src",
    "width",
    "height",
    "frameborder",
    "allow",
    "allowfullscreen",
    "loading",
    "referrerpolicy",
    "title",
  ]),
  div: new Set(["data-youtube-video"]),
  table: new Set(["cellpadding", "cellspacing"]),
  th: new Set(["colspan", "rowspan", "colwidth"]),
  td: new Set(["colspan", "rowspan", "colwidth"]),
  col: new Set(["span"]),
};

const LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const IMAGE_PROTOCOLS = new Set(["https:", "http:"]);
const IMAGE_SIZE_VALUES = new Set(["sm", "md", "lg", "full"]);
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
]);

const TEXT_ALIGN_VALUES = new Set(["left", "right", "center", "justify"]);
const COLOR_VALUE_REGEX =
  /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]+)$/i;
const CLASS_TOKEN_REGEX = /^[A-Za-z0-9_-]+$/;
const URI_SCHEME_REGEX = /^[A-Za-z][A-Za-z0-9+.-]*:/;

const isLocalHttpHost = (host) =>
  host === "localhost" || host === "127.0.0.1" || host === "::1";

const normalizeUrl = (value) => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return null;
  if (!URI_SCHEME_REGEX.test(raw)) return null;
  try {
    return new URL(raw);
  } catch (_error) {
    return null;
  }
};

const sanitizeColwidthAttribute = (colwidthValue) => {
  if (typeof colwidthValue !== "string" || !colwidthValue.trim()) return "";
  const values = colwidthValue
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);
  if (values.length === 0) return "";

  const normalized = [];
  for (const token of values) {
    if (!/^\d+$/.test(token)) return "";
    const width = Number.parseInt(token, 10);
    if (!Number.isFinite(width) || width <= 0) return "";
    normalized.push(String(width));
  }
  return normalized.join(",");
};

const sanitizeStyleAttribute = (styleValue) => {
  if (typeof styleValue !== "string" || !styleValue.trim()) return "";
  const out = [];
  const declarations = styleValue.split(";");
  for (const declaration of declarations) {
    if (!declaration.trim()) continue;
    const idx = declaration.indexOf(":");
    if (idx === -1) continue;
    const property = declaration.slice(0, idx).trim().toLowerCase();
    const value = declaration.slice(idx + 1).trim();
    if (!value) continue;

    if (property === "text-align") {
      const normalized = value.toLowerCase();
      if (TEXT_ALIGN_VALUES.has(normalized)) {
        out.push(`${property}: ${normalized}`);
      }
      continue;
    }

    if (property === "color" || property === "background-color") {
      if (COLOR_VALUE_REGEX.test(value)) {
        out.push(`${property}: ${value}`);
      }
    }
  }
  return out.join("; ");
};

const sanitizeClassAttribute = (classValue) => {
  if (typeof classValue !== "string" || !classValue.trim()) return "";
  const safeTokens = classValue
    .split(/\s+/)
    .filter((token) => CLASS_TOKEN_REGEX.test(token));
  return safeTokens.join(" ");
};

const sanitizeLinkHref = (hrefValue) => {
  const raw = typeof hrefValue === "string" ? hrefValue.trim() : "";
  if (!raw) return "";
  if (
    raw.startsWith("/") ||
    raw.startsWith("./") ||
    raw.startsWith("../") ||
    raw.startsWith("#")
  ) {
    return raw;
  }
  const parsed = normalizeUrl(raw);
  if (!parsed) return "";
  if (!LINK_PROTOCOLS.has(parsed.protocol)) return "";
  return parsed.href;
};

export const sanitizeImageUrl = (srcValue) => {
  const parsed = normalizeUrl(srcValue);
  if (!parsed) return "";
  if (!IMAGE_PROTOCOLS.has(parsed.protocol)) return "";
  if (parsed.protocol === "http:" && !isLocalHttpHost(parsed.hostname)) return "";
  return parsed.href;
};

const sanitizeImageSize = (sizeValue) => {
  if (typeof sizeValue !== "string") return "";
  const normalized = sizeValue.trim().toLowerCase();
  return IMAGE_SIZE_VALUES.has(normalized) ? normalized : "";
};

const sanitizeIframeSrc = (srcValue) => {
  const parsed = normalizeUrl(srcValue);
  if (!parsed) return "";
  if (parsed.protocol !== "https:") return "";
  const host = parsed.hostname.toLowerCase();
  if (!YOUTUBE_HOSTS.has(host)) return "";
  if (host !== "youtu.be" && !parsed.pathname.includes("/embed/")) return "";
  return parsed.href;
};

const sanitizeElementAttributes = (element) => {
  const tag = element.tagName.toLowerCase();
  const allowedForTag = ALLOWED_ATTRIBUTES_BY_TAG[tag] || new Set();
  const attrs = Array.from(element.attributes);

  for (const attr of attrs) {
    const name = attr.name.toLowerCase();
    const value = attr.value;

    if (name.startsWith("on")) {
      element.removeAttribute(attr.name);
      continue;
    }

    const isAllowed =
      allowedForTag.has(name) || ALLOWED_GLOBAL_ATTRIBUTES.has(name);

    if (!isAllowed) {
      element.removeAttribute(attr.name);
      continue;
    }

    if (name === "style") {
      const safeStyle = sanitizeStyleAttribute(value);
      if (safeStyle) {
        element.setAttribute("style", safeStyle);
      } else {
        element.removeAttribute("style");
      }
      continue;
    }

    if (name === "class") {
      const safeClass = sanitizeClassAttribute(value);
      if (safeClass) {
        element.setAttribute("class", safeClass);
      } else {
        element.removeAttribute("class");
      }
      continue;
    }

    if (tag === "a" && name === "href") {
      const safeHref = sanitizeLinkHref(value);
      if (safeHref) {
        element.setAttribute("href", safeHref);
      } else {
        element.removeAttribute("href");
      }
      continue;
    }

    if (tag === "a" && name === "target") {
      const normalized = value === "_blank" ? "_blank" : "_self";
      element.setAttribute("target", normalized);
      continue;
    }

    if (tag === "a" && name === "rel") {
      continue;
    }

    if (tag === "img" && name === "src") {
      const safeSrc = sanitizeImageUrl(value);
      if (safeSrc) {
        element.setAttribute("src", safeSrc);
      } else {
        element.removeAttribute("src");
      }
      continue;
    }

    if (tag === "img" && name === "data-size") {
      const safeSize = sanitizeImageSize(value);
      if (safeSize) {
        element.setAttribute("data-size", safeSize);
      } else {
        element.removeAttribute("data-size");
      }
      continue;
    }

    if (tag === "iframe" && name === "src") {
      const safeSrc = sanitizeIframeSrc(value);
      if (safeSrc) {
        element.setAttribute("src", safeSrc);
      } else {
        element.removeAttribute("src");
      }
      continue;
    }

    if ((tag === "th" || tag === "td") && name === "colwidth") {
      const safeColwidth = sanitizeColwidthAttribute(value);
      if (safeColwidth) {
        element.setAttribute("colwidth", safeColwidth);
      } else {
        element.removeAttribute("colwidth");
      }
    }
  }

  if (tag === "a") {
    const href = element.getAttribute("href");
    if (!href) {
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        parent.removeChild(element);
      }
      return false;
    }

    if (element.getAttribute("target") === "_blank") {
      element.setAttribute("rel", "noopener noreferrer");
    } else if (element.hasAttribute("rel")) {
      element.removeAttribute("rel");
    }
  }

  if (tag === "img") {
    const src = element.getAttribute("src");
    if (!src) {
      element.remove();
      return false;
    }
    element.setAttribute("loading", "lazy");
    element.setAttribute("decoding", "async");
  }

  if (tag === "iframe") {
    const src = element.getAttribute("src");
    if (!src) {
      element.remove();
      return false;
    }
    element.setAttribute("loading", "lazy");
    element.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    if (!element.hasAttribute("allowfullscreen")) {
      element.setAttribute("allowfullscreen", "");
    }
  }

  return true;
};

const sanitizeNode = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node;
  const tag = element.tagName.toLowerCase();

  if (!ALLOWED_TAGS.has(tag)) {
    if (DROP_CONTENT_TAGS.has(tag)) {
      element.remove();
      return;
    }

    const parent = element.parentNode;
    if (!parent) return;
    const children = Array.from(element.childNodes);
    children.forEach((child) => parent.insertBefore(child, element));
    parent.removeChild(element);
    children.forEach((child) => sanitizeNode(child));
    return;
  }

  const children = Array.from(element.childNodes);
  const keepElement = sanitizeElementAttributes(element);
  if (!keepElement) {
    children.forEach((child) => sanitizeNode(child));
    return;
  }

  children.forEach((child) => sanitizeNode(child));
};

export const sanitizeExplanationHtml = (html) => {
  if (typeof html !== "string" || html.trim() === "") return "";
  if (typeof DOMParser === "undefined") return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const bodyChildren = Array.from(doc.body.childNodes);
  bodyChildren.forEach((child) => sanitizeNode(child));
  return doc.body.innerHTML;
};

export const sanitizeCaptionHtml = (html) => {
  if (typeof html !== "string" || html.trim() === "") return "";
  const sanitized = sanitizeExplanationHtml(html);
  return sanitized;
};

export const sanitizeInlineImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  const seen = new Set();
  const safe = [];
  for (const rawUrl of urls) {
    const clean = sanitizeImageUrl(rawUrl);
    if (!clean || seen.has(clean)) continue;
    seen.add(clean);
    safe.push(clean);
  }
  return safe;
};
